import { QueryClientProvider, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { sessionEndedBy } from '@/features/auth/sessionEnd';
import { refetchForLocale } from '@/shared/api/localeRefetch';
import { ToastProvider } from '@/shared/components/ToastProvider';
import { applyDocumentLocale, isLocale } from '@/shared/i18n';
import { useLocale } from '@/shared/i18n/useLocale';

import { createQueryClient } from './queryClient';

/** Keeps <html lang/dir> aligned with the active language. */
function DirectionSync({ children }: { children: ReactNode }) {
  const { locale } = useLocale();

  useEffect(() => {
    applyDocumentLocale(locale);
  }, [locale]);

  return children;
}

/**
 * EX-10 — a language switch re-asks the server for what is on screen, in the
 * new language, and changes nothing else (see `refetchForLocale`).
 *
 * The document language is set first, so every request this starts carries
 * the new `X-Fidelen-Locale` whichever listener the i18n module calls first.
 * Nothing is reloaded or remounted: a draft being typed, the tab, the filters
 * and the page stay as they are.
 */
function LocaleQuerySync() {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();

  useEffect(() => {
    const onLanguageChanged = (language: string) => {
      if (isLocale(language)) applyDocumentLocale(language);
      void refetchForLocale(queryClient);
    };

    i18n.on('languageChanged', onLanguageChanged);

    return () => i18n.off('languageChanged', onLanguageChanged);
  }, [i18n, queryClient]);

  return null;
}

/**
 * A data read answered 401/419 means the session ended under the page: that
 * session is asked again, so its gate shows the sign-in instead of a shell
 * whose every read now fails (w9-to-w6-1; see `sessionEndedBy`).
 */
function SessionEndSync() {
  const queryClient = useQueryClient();

  useEffect(
    () =>
      queryClient.getQueryCache().subscribe((event) => {
        if (event.type !== 'updated' || event.action.type !== 'error') return;

        const failure: unknown = event.action.error;
        // The cache types its queries loosely (`Query<any, …>`); a key is a key.
        const key = event.query.queryKey as QueryKey;
        const session = sessionEndedBy(key, failure);

        if (session !== null) void queryClient.resetQueries({ queryKey: session, exact: true });
      }),
    [queryClient],
  );

  return null;
}

export function AppProviders({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <LocaleQuerySync />
      <SessionEndSync />
      <DirectionSync>
        <ToastProvider>{children}</ToastProvider>
      </DirectionSync>
    </QueryClientProvider>
  );
}
